import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import Popover from '@material-ui/core/Popover'
import { makeStyles } from '@material-ui/core/styles'
import {
  EmailShareButton,
  FacebookShareButton,
  FacebookIcon,
  EmailIcon,
  TwitterShareButton,
} from 'react-share'
import MailOutlineIcon from 'components/material-symbol/icons/MailOutline'
import {
  menuItemStyles,
  menuListStyles,
  menuPaperStyles,
} from 'lib/styles/menu'
import XShareButton from './x-share-button'
import { XButton } from './x-share-button/x-button'

const useStyles = makeStyles({
  menuPaper: {
    ...menuPaperStyles,
    minWidth: 200,
  },
  menuList: menuListStyles,
  // Rows are react-share <button>s, which shrink-wrap (MenuItem sets
  // `width: auto`). Fill the list (which already carries the shared inset) so
  // the hover/focus highlight spans the menu like the other dropdown menus.
  menuItem: {
    ...menuItemStyles,
    display: 'flex',
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 44,
    // The shared item styles restate minHeight at `sm`; keep the taller row.
    '@media (min-width:600px)': {
      minHeight: 44,
    },
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  menuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
  menuEmailIcon: {
    fontSize: 20,
  },
})

// `variant="icons"` (default) is the original row of round brand icons.
// `variant="menu"` matches the "Social" popover in the booking share dialog
// (`components/share-box/booking-share-actions`): a shared-menu-look popover
// with labeled Facebook / X / Email rows.
const SocialSharePopover = ({ trigger, text, url, variant }) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)

  const openPopover = e => setAnchorEl(e.currentTarget)
  const closePopover = () => setAnchorEl(null)

  const isOpen = Boolean(anchorEl)
  const id = isOpen ? 'social-share-popover' : undefined
  const isMenu = variant === 'menu'

  return (
    <>
      {trigger && <trigger.type {...trigger.props} onClick={openPopover} />}
      <Popover
        id={id}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: isMenu ? 'bottom' : 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: isMenu ? 'top' : 'bottom',
          horizontal: 'center',
        }}
        classes={isMenu ? { paper: classes.menuPaper } : undefined}
      >
        {isMenu ? (
          <MenuList
            autoFocusItem
            onClick={closePopover}
            classes={{ root: classes.menuList }}
          >
            <MenuItem
              button
              component={FacebookShareButton}
              className={classes.menuItem}
              // MenuItem owns the row chrome; skip react-share's inline reset
              // (padding: 0, transparent bg) so hover and spacing apply.
              resetButtonStyle={false}
              aria-label="Facebook"
              url={url}
              quote={text}
            >
              <ListItemIcon className={classes.menuIcon}>
                <FacebookIcon size={20} round />
              </ListItemIcon>
              <ListItemText primary="Facebook" />
            </MenuItem>
            <MenuItem
              button
              component={TwitterShareButton}
              className={classes.menuItem}
              // MenuItem owns the row chrome; skip react-share's inline reset
              // (padding: 0, transparent bg) so hover and spacing apply.
              resetButtonStyle={false}
              aria-label="X"
              url={url}
              title={text}
            >
              <ListItemIcon className={classes.menuIcon}>
                <XButton size={20} />
              </ListItemIcon>
              <ListItemText primary="X" />
            </MenuItem>
            <MenuItem
              button
              component={EmailShareButton}
              className={classes.menuItem}
              // MenuItem owns the row chrome; skip react-share's inline reset
              // (padding: 0, transparent bg) so hover and spacing apply.
              resetButtonStyle={false}
              aria-label="Email"
              url={url}
              subject={text}
            >
              <ListItemIcon className={classes.menuIcon}>
                <MailOutlineIcon
                  className={classes.menuEmailIcon}
                  aria-hidden
                />
              </ListItemIcon>
              <ListItemText primary="Email" />
            </MenuItem>
          </MenuList>
        ) : (
          <Box p={1} display="flex">
            <Box mr={1}>
              <FacebookShareButton url={url} quote={text}>
                <FacebookIcon size={32} round />
              </FacebookShareButton>
            </Box>
            <Box mr={1}>
              <XShareButton url={url} title={text} buttonSize={40} />
            </Box>
            <EmailShareButton url={url} subject={text}>
              <EmailIcon size={32} round />
            </EmailShareButton>
          </Box>
        )}
      </Popover>
    </>
  )
}

SocialSharePopover.propTypes = {
  trigger: PropTypes.element,
  text: PropTypes.string,
  url: PropTypes.string,
  variant: PropTypes.oneOf(['icons', 'menu']),
}

SocialSharePopover.defaultProps = {
  variant: 'icons',
}

export default SocialSharePopover
